import { useState } from "react";
import { X as XIcon } from "lucide-react";
import { Button, Pill } from "./primitives";
import { cn } from "../lib/cn";
import type { Mount, MountMode } from "../lib/types";

function modeTone(mode: MountMode): "neutral" | "warning" | "danger" {
  return mode === "rw" ? "warning" : mode === "void" ? "danger" : "neutral";
}

/**
 * Editor for one sandbox-mount tier. Shows inherited rows (from tiers to the
 * left — read-only) above this tier's own editable rows. `void` cancels a path
 * a left tier set.
 */
export function MountsEditor({
  own,
  inherited = [],
  onAdd,
  onRemove,
  hint,
}: {
  own: Mount[];
  inherited?: (Mount & { source: string })[];
  onAdd: (path: string, mode: MountMode) => void;
  onRemove: (path: string) => void;
  hint: string;
}) {
  const [path, setPath] = useState("");
  const [mode, setMode] = useState<MountMode>("ro");
  return (
    <div className="rounded-lg border border-border bg-surface-2/40 p-3">
      <div className="text-[11px] font-medium text-fg-muted mb-1.5">
        Sandbox mounts <span className="text-fg-subtle">— {hint}</span>
      </div>
      {inherited.length > 0 && (
        <div className="space-y-1 mb-2">
          {inherited.map((m) => (
            <div key={`inh:${m.path}`} className="flex items-center gap-2 text-[11px] font-mono opacity-70">
              <Pill tone={modeTone(m.mode)}>{m.mode}</Pill>
              <span className="text-fg truncate flex-1">{m.path}</span>
              <span className="text-fg-subtle not-italic">{m.source}</span>
            </div>
          ))}
        </div>
      )}
      {own.length > 0 && (
        <div className="space-y-1 mb-2">
          {own.map((m) => (
            <div key={m.path} className="flex items-center gap-2 text-[11px] font-mono">
              <Pill tone={modeTone(m.mode)}>{m.mode}</Pill>
              <span className={cn("truncate flex-1", m.mode === "void" ? "text-fg-subtle line-through" : "text-fg")}>{m.path}</span>
              <button className="text-fg-subtle hover:text-danger" onClick={() => onRemove(m.path)} aria-label="remove mount"><XIcon className="size-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <input value={path} onChange={(e) => setPath(e.target.value)} placeholder="/abs/path/to/mount" className="flex-1 h-7 text-xs font-mono" />
        <select value={mode} onChange={(e) => setMode(e.target.value as MountMode)} className="h-7 text-xs w-20" title="ro / rw / void (void cancels an inherited mount)">
          <option value="ro">ro</option>
          <option value="rw">rw</option>
          <option value="void">void</option>
        </select>
        <Button size="sm" disabled={!path.trim().startsWith("/")} onClick={() => { onAdd(path.trim(), mode); setPath(""); }}>Add</Button>
      </div>
    </div>
  );
}
